import { useRef, useEffect, forwardRef, useImperativeHandle } from "react";

/**
 * <MultiLineInput> — Textarea auto-grow qui se comporte comme un Input :
 *   - **Enter** = focus champ suivant (tabbing naturel) — comme un formulaire
 *   - **Shift + Enter** = nouvelle ligne (préserve le \n dans la valeur)
 *   - **Esc** = blur
 *   - Hauteur s'ajuste automatiquement au contenu
 *
 * Props mêmes que <textarea> + className. Idéal pour les libellés de lignes
 * de devis / factures où un retour à la ligne est parfois nécessaire.
 *
 * Les sauts de ligne sont préservés et restitués tels quels dans les PDF
 * (ReportLab respecte les '\n' avec replaceParaWithLineBreaks).
 */
const MultiLineInput = forwardRef(function MultiLineInput(
  { value, onChange, onBlur, onKeyDown, className = "", placeholder, minRows = 1, maxRows = 6, "data-testid": testId, disabled, ...rest },
  ref,
) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current);

  // Auto-resize : ajuste la hauteur à scrollHeight (borné par maxRows)
  const resize = () => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineH = parseFloat(getComputedStyle(el).lineHeight) || 18;
    const padTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
    const padBot = parseFloat(getComputedStyle(el).paddingBottom) || 0;
    const maxH = lineH * maxRows + padTop + padBot;
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
  };
  useEffect(() => { resize(); /* eslint-disable-next-line */ }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Comportement Input : tab vers le champ focusable suivant
      e.preventDefault();
      const form = e.currentTarget.closest("form, table, body");
      if (form) {
        const focusables = Array.from(
          form.querySelectorAll(
            'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => el.offsetParent !== null);
        const idx = focusables.indexOf(e.currentTarget);
        if (idx >= 0 && idx + 1 < focusables.length) {
          focusables[idx + 1].focus();
        }
      }
      return;
    }
    if (e.key === "Escape") {
      e.currentTarget.blur();
      return;
    }
    onKeyDown?.(e);
  };

  return (
    <textarea
      ref={innerRef}
      rows={minRows}
      value={value ?? ""}
      onChange={(e) => { onChange?.(e); /* resize après render via useEffect */ }}
      onBlur={onBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testId}
      className={`flex w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden leading-[1.4] ${className}`}
      {...rest}
    />
  );
});

export default MultiLineInput;
